import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { X, Copy, Check, Share2, Trash2, Loader2, Printer, ExternalLink, Plus } from "lucide-react";

// Modal: mint / list / revoke shareable traffic-report links + one-click
// Print-to-PDF of the live dashboard.
export default function TrafficShareModal({ open, onClose }) {
    const [shares, setShares] = useState([]);
    const [loading, setLoading] = useState(false);
    const [label, setLabel] = useState("");
    const [expiry, setExpiry] = useState("never");
    const [creating, setCreating] = useState(false);
    const [copiedToken, setCopiedToken] = useState(null);

    const reload = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/traffic/shares");
            setShares(data);
        } catch (e) {
            toast.error("Couldn't load shares.");
        } finally { setLoading(false); }
    };

    useEffect(() => { if (open) reload(); }, [open]);

    if (!open) return null;

    const baseUrl = window.location.origin;
    const linkFor = (token) => `${baseUrl}/r/${token}`;

    const onCreate = async (e) => {
        e?.preventDefault();
        setCreating(true);
        try {
            const expDays = expiry === "never" ? null : Number(expiry);
            const { data } = await api.post("/admin/traffic/shares", {
                label: label.trim(),
                expires_in_days: expDays,
            });
            toast.success("Share link created.");
            setLabel("");
            await reload();
            // Auto-copy the new link to clipboard for convenience
            try {
                await navigator.clipboard.writeText(linkFor(data.token));
                setCopiedToken(data.token);
                setTimeout(() => setCopiedToken(null), 2000);
            } catch { /* clipboard blocked — user can still copy manually */ }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't create share.");
        } finally { setCreating(false); }
    };

    const onCopy = async (token) => {
        try {
            await navigator.clipboard.writeText(linkFor(token));
            setCopiedToken(token);
            setTimeout(() => setCopiedToken(null), 1800);
        } catch {
            toast.error("Couldn't copy. Long-press the link instead.");
        }
    };

    const onRevoke = async (token, label) => {
        if (!window.confirm(`Revoke "${label}"? The public link will stop working immediately.`)) return;
        try {
            await api.delete(`/admin/traffic/shares/${token}`);
            toast.success("Share revoked.");
            await reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't revoke.");
        }
    };

    const onPrint = () => {
        // Close the modal, then trigger the browser's print → save-as-PDF UI.
        onClose();
        setTimeout(() => window.print(), 400);
    };

    const visible = shares.filter((s) => !s.revoked);
    const revoked = shares.filter((s) => s.revoked);

    return (
        <div className="fixed inset-0 z-[80] flex items-start sm:items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto" data-testid="traffic-share-modal">
            <div className="bg-background border border-border rounded-2xl max-w-2xl w-full p-5 sm:p-6 shadow-2xl my-8">
                <div className="flex items-start justify-between gap-3 mb-4">
                    <div>
                        <h2 className="font-serif text-xl font-semibold flex items-center gap-2">
                            <Share2 className="h-5 w-5 text-primary" /> Share traffic report
                        </h2>
                        <p className="text-xs text-muted-foreground mt-1 max-w-md">
                            Mint a public, read-only link to this dashboard you can send to attorneys, investors or banks. Anyone with the link sees live numbers — no login.
                        </p>
                    </div>
                    <button onClick={onClose} className="rounded-full p-1.5 hover:bg-accent" aria-label="Close" data-testid="share-modal-close">
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <form onSubmit={onCreate} className="ll-card mb-5 space-y-3" data-testid="share-create-form">
                    <div>
                        <label className="ll-label mb-1.5 block">Label (private — for your records)</label>
                        <input
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            placeholder="e.g. Pitch deck — Smith & Sons LLP"
                            className="ll-input"
                            maxLength={120}
                            data-testid="share-label-input"
                        />
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                        <div>
                            <label className="ll-label mb-1.5 block">Expires</label>
                            <select value={expiry} onChange={(e) => setExpiry(e.target.value)}
                                    className="ll-input" data-testid="share-expiry-select">
                                <option value="never">Never (until revoked)</option>
                                <option value="7">In 7 days</option>
                                <option value="14">In 14 days</option>
                                <option value="30">In 30 days</option>
                                <option value="90">In 90 days</option>
                            </select>
                        </div>
                        <button type="submit" disabled={creating}
                                className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 ml-auto"
                                data-testid="share-create-btn">
                            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                            Create link
                        </button>
                    </div>
                </form>

                {/* Quick action: print to PDF */}
                <button onClick={onPrint}
                        className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-border px-4 py-2.5 text-sm font-semibold hover:bg-accent mb-5"
                        data-testid="share-print-btn">
                    <Printer className="h-4 w-4" />
                    Export this dashboard as PDF (browser print)
                </button>

                <h3 className="ll-label mb-2">Active share links {visible.length > 0 && <span className="text-foreground/70">({visible.length})</span>}</h3>
                {loading ? (
                    <div className="flex items-center justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
                ) : visible.length === 0 ? (
                    <div className="ll-card text-sm text-muted-foreground py-3" data-testid="share-empty">
                        No active links yet. Mint one above — copy it and drop it into an email or DM.
                    </div>
                ) : (
                    <ul className="space-y-2" data-testid="share-list">
                        {visible.map((s) => (
                            <li key={s.token} className="ll-card" data-testid={`share-row-${s.token}`}>
                                <div className="flex items-start justify-between gap-3 mb-1.5 flex-wrap">
                                    <div className="min-w-0 flex-1">
                                        <div className="font-semibold text-sm truncate">{s.label || "Untitled"}</div>
                                        <div className="text-[11px] text-muted-foreground mt-0.5">
                                            Created {new Date(s.created_at).toLocaleDateString()}
                                            {s.expires_at && <> · Expires {new Date(s.expires_at).toLocaleDateString()}</>}
                                            {!s.expires_at && <> · No expiry</>}
                                            <> · {s.view_count || 0} view{s.view_count === 1 ? "" : "s"}</>
                                        </div>
                                    </div>
                                    <button onClick={() => onRevoke(s.token, s.label)}
                                            className="text-[11px] text-destructive inline-flex items-center gap-1 hover:underline"
                                            data-testid={`share-revoke-${s.token}`}>
                                        <Trash2 className="h-3 w-3" /> Revoke
                                    </button>
                                </div>
                                <div className="flex items-center gap-2">
                                    <code className="flex-1 text-[11px] font-mono bg-muted px-2.5 py-1.5 rounded-md break-all select-all">
                                        {linkFor(s.token)}
                                    </code>
                                    <button onClick={() => onCopy(s.token)}
                                            className={`flex-shrink-0 inline-flex items-center gap-1 rounded-full text-[11px] px-3 py-1.5 font-semibold transition-colors ${copiedToken === s.token ? "bg-green-500 text-white" : "bg-primary text-primary-foreground hover:bg-primary/90"}`}
                                            data-testid={`share-copy-${s.token}`}>
                                        {copiedToken === s.token ? <><Check className="h-3 w-3" /> Copied</> : <><Copy className="h-3 w-3" /> Copy</>}
                                    </button>
                                    <a href={linkFor(s.token)} target="_blank" rel="noopener noreferrer"
                                       className="flex-shrink-0 inline-flex items-center gap-1 rounded-full border border-border text-[11px] px-3 py-1.5 hover:bg-accent"
                                       data-testid={`share-open-${s.token}`}>
                                        <ExternalLink className="h-3 w-3" /> Open
                                    </a>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}

                {revoked.length > 0 && (
                    <details className="mt-4">
                        <summary className="text-[11px] text-muted-foreground cursor-pointer hover:text-foreground">Show {revoked.length} revoked link{revoked.length === 1 ? "" : "s"}</summary>
                        <ul className="space-y-1.5 mt-2">
                            {revoked.map((s) => (
                                <li key={s.token} className="text-[11px] text-muted-foreground flex items-center justify-between gap-3 px-3 py-1.5 rounded-md bg-muted/50">
                                    <span className="truncate">{s.label}</span>
                                    <span className="flex-shrink-0">revoked {new Date(s.revoked_at).toLocaleDateString()}</span>
                                </li>
                            ))}
                        </ul>
                    </details>
                )}
            </div>
        </div>
    );
}
